import { Widget, FormValue } from '@truvity/widget__forms';

import { VcPreview, CredentialDid, ViewWidgetErrors, WalletDid, EntityDid, WidgetViewApi } from '../../types';
import { getNetworkErrorsDescriptor, isNotFound } from '../../utils/error';

import { ReadonlyFileApi } from './apiFilesStorage';

export type Preview = string;

export type Credential = {
    Preview: Preview;
    VcDid: CredentialDid;
};

export type Credentials = Credential[];

export type LinkedCredentialsPreviewRequests = {
    /**
     * A handler that resolves a DID of the linked credential to a human-readable preview.
     */
    getVcPreview: (credentialDid: CredentialDid) => Promise<VcPreview>;
};

export type LinkedCredentialsSuggestRequests = {
    /**
     * A handler that returns linked credential suggestions based on the JSON Schema IDs configured for the field
     * and existing claim values that can be used for conditional logic.
     */
    suggestVc: (schemaIds: string[], claimValues: FormValue) => Promise<VcPreview[]>;
};

export type LinkedCredentialsApi = LinkedCredentialsPreviewRequests &
    LinkedCredentialsSuggestRequests & {
        /**
         *  Widget API client. Can be customized for tests.
         */
        widgetApi: WidgetViewApi;

        /**
         *  Wallet API client. Can be customized for tests.
         */
        walletApi: ReadonlyFileApi;
    };

export type LinkedCredentialsWidgetOptions = LinkedCredentialsApi & {
    walletDid: WalletDid;
    entityDid: EntityDid;
    onError?: (error: ViewWidgetErrors) => void;
};

export class LinkedCredentialsWidget extends Widget {
    constructor(private options: LinkedCredentialsWidgetOptions) {
        super();
    }

    protected handleErrors(error: any): void {
        this.options.onError?.(getNetworkErrorsDescriptor(error));
    }

    protected handleCredentialErrors(credentialId: CredentialDid, error: any): void {
        if (isNotFound(error)) {
            this.options.onError?.({
                code: 'ERRCREDENTIALNOTFOUND',
                credentialId,
            });
        } else {
            this.handleErrors(error);
        }
    }

    protected async getCredentialPreview(id: string): Promise<Preview> {
        const { getVcPreview } = this.options;

        try {
            const { Preview } = await getVcPreview(id);
            return Preview;
        } catch (error) {
            this.handleCredentialErrors(id, error);
            return 'N/A';
        }
    }

    protected async suggestCredentials(type: string[]): Promise<Credentials> {
        const { suggestVc } = this.options;
        const { values } = this.params.form.getActualValues();

        try {
            return suggestVc(type, values);
        } catch (error) {
            this.handleErrors(error);
            return [];
        }
    }

    getFieldApi() {
        return {
            options: this.options,
            getCredentialPreview: this.getCredentialPreview.bind(this),
            suggestCredentials: this.suggestCredentials.bind(this),
        };
    }
}
